import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Alert } from '../components/shared/Alert';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { TruckIcon, Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import { authApi } from '../services/api';
import { logger } from '../utils/logger';
import '../styles/modern.css';

export const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Invalid reset link. Please request a new password reset.');
        setVerifying(false);
        return;
      }

      try {
        await authApi.verifyResetToken(token);
        setTokenValid(true);
      } catch (err: unknown) {
        logger.error('Token verification failed:', err);
        const errorMessage = err instanceof Error ? err.message : 'Invalid or expired reset link. Please request a new password reset.';
        setError(errorMessage);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (pass: string): string[] => {
    const errors: string[] = [];

    // Must match backend auth.service.ts validation requirements
    if (pass.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pass)) errors.push('At least one uppercase letter');
    if (!/[a-z]/.test(pass)) errors.push('At least one lowercase letter');
    if (!/[0-9]/.test(pass)) errors.push('At least one number');
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass)) errors.push('At least one special character (!@#$%^&*)');
    if (/(.)\1{2,}/.test(pass)) errors.push('No 3+ repeated characters');
    if (/(?:abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|012|123|234|345|456|567|678|789)/i.test(pass)) errors.push('No sequential characters (abc, 123)');

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validate password
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setError(`Password requirements: ${passwordErrors.join(', ')}`);
      return;
    }

    // Check passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      if (!token) {
        setError('Invalid reset link');
        return;
      }
      await authApi.resetPassword(token, password, confirmPassword);
      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: unknown) {
      logger.error('Password reset failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to reset password. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Loading state while verifying token
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-gray-50)' }}>
        <div className="text-center">
          <LoadingSpinner message="Verifying reset link..." size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex" style={{ background: 'var(--color-gray-50)' }}>
      {/* Left Side - Branding & Imagery */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{
        background: 'var(--color-primary)',
        position: 'relative'
      }}>
        {/* Geometric Pattern Background */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />

        {/* Content Container */}
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <div style={{
              background: 'rgba(255, 255, 255, 0.15)',
              padding: '12px',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <TruckIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Transportation Safety</h1>
              <p className="text-blue-100 text-sm">Platform</p>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl font-bold mb-4 leading-tight">
                Create New Password
              </h2>
              <p className="text-xl text-blue-100">
                Choose a strong, unique password to secure your account.
              </p>
            </div>

            {/* Password Tips */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              padding: '20px',
              borderRadius: '12px',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <p className="text-sm text-blue-100 mb-2">Password Tips</p>
              <ul className="text-sm text-white space-y-2">
                <li>Use at least 12 characters</li>
                <li>Mix uppercase and lowercase letters</li>
                <li>Include numbers and special characters</li>
                <li>Don't reuse passwords from other sites</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="text-sm text-blue-100">
            {new Date().getFullYear()} Transportation Safety Platform. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8 text-center">
            <div className="flex justify-center items-center gap-3 mb-2">
              <div style={{
                background: 'var(--color-primary)',
                padding: '10px',
                borderRadius: '10px'
              }}>
                <TruckIcon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold" style={{ color: 'var(--color-gray-900)' }}>
                Transportation Safety
              </h1>
            </div>
          </div>

          {/* Card */}
          <div style={{
            background: '#fbf9f4',
            borderRadius: 'var(--radius-xl)',
            padding: '48px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)',
            border: '1px solid var(--color-gray-200)'
          }} className="fade-in-up">

            {success ? (
              /* Success State */
              <div className="text-center">
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'var(--color-success)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <CheckCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-gray-900)' }}>
                  Password Reset Successfully!
                </h2>
                <p className="text-muted mb-6">
                  Your password has been changed. You can now log in with your new password.
                </p>
                <p className="text-sm text-muted mb-8">
                  Redirecting to login page in a few seconds...
                </p>
                <Link
                  to="/login"
                  className="btn-primary"
                  style={{
                    display: 'inline-block',
                    padding: '12px 32px',
                    fontSize: '15px',
                    fontWeight: 600
                  }}
                >
                  Go to Login
                </Link>
              </div>
            ) : !tokenValid ? (
              /* Invalid Token State */
              <div className="text-center">
                <div style={{
                  width: '64px',
                  height: '64px',
                  background: 'var(--color-danger)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <XCircle className="w-8 h-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-gray-900)' }}>
                  Invalid Reset Link
                </h2>
                <p className="text-muted mb-6">
                  {error || 'This password reset link is invalid or has expired.'}
                </p>
                <Link
                  to="/forgot-password"
                  className="btn-primary"
                  style={{
                    display: 'inline-block',
                    padding: '12px 32px',
                    fontSize: '15px',
                    fontWeight: 600
                  }}
                >
                  Request New Reset Link
                </Link>
                <div className="mt-4">
                  <Link
                    to="/login"
                    className="text-sm"
                    style={{ color: 'var(--color-gray-600)' }}
                  >
                    Back to login
                  </Link>
                </div>
              </div>
            ) : (
              /* Form State */
              <>
                {/* Back Link */}
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-sm mb-6"
                  style={{ color: 'var(--color-gray-600)' }}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </Link>

                {/* Header */}
                <div className="mb-8">
                  <div style={{
                    width: '56px',
                    height: '56px',
                    background: 'var(--color-primary)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <Lock className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--color-gray-900)' }}>
                    Set new password
                  </h2>
                  <p className="text-muted">
                    Your new password must be at least 12 characters with uppercase, lowercase, numbers, and special characters.
                  </p>
                </div>

                {/* Error Alert */}
                {error && (
                  <div className="mb-6">
                    <Alert type="danger" message={error} onClose={() => setError('')} />
                  </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Password Input */}
                  <div>
                    <label
                      htmlFor="password"
                      className="block text-sm font-semibold mb-2"
                      style={{ color: 'var(--color-gray-700)' }}
                    >
                      New Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your new password"
                        style={{
                          width: '100%',
                          padding: '12px 48px 12px 16px',
                          fontSize: '15px',
                          border: '1px solid var(--color-gray-300)',
                          borderRadius: 'var(--radius-lg)',
                          transition: 'all 0.15s ease',
                          color: 'var(--color-gray-900)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--color-primary)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(35, 33, 29, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--color-gray-300)';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: 'var(--color-gray-500)'
                        }}
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password Input */}
                  <div>
                    <label
                      htmlFor="confirmPassword"
                      className="block text-sm font-semibold mb-2"
                      style={{ color: 'var(--color-gray-700)' }}
                    >
                      Confirm Password
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        id="confirmPassword"
                        name="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your new password"
                        style={{
                          width: '100%',
                          padding: '12px 48px 12px 16px',
                          fontSize: '15px',
                          border: '1px solid var(--color-gray-300)',
                          borderRadius: 'var(--radius-lg)',
                          transition: 'all 0.15s ease',
                          color: 'var(--color-gray-900)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = 'var(--color-primary)';
                          e.target.style.boxShadow = '0 0 0 3px rgba(35, 33, 29, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = 'var(--color-gray-300)';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          color: 'var(--color-gray-500)'
                        }}
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="mt-2 text-sm" style={{ color: 'var(--color-danger)' }}>
                        Passwords do not match
                      </p>
                    )}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || password !== confirmPassword || validatePassword(password).length > 0}
                    className="btn-primary"
                    style={{
                      width: '100%',
                      height: '48px',
                      fontSize: '15px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      opacity: (loading || password !== confirmPassword || validatePassword(password).length > 0) ? 0.6 : 1,
                      cursor: (loading || password !== confirmPassword || validatePassword(password).length > 0) ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? (
                      <>
                        <LoadingSpinner message="Resetting..." size="sm" />
                        <span>Resetting password...</span>
                      </>
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        <span>Reset Password</span>
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* Footer Links */}
          <div className="mt-8 text-center">
            <div className="flex items-center justify-center gap-6 text-sm" style={{ color: 'var(--color-gray-500)' }}>
              <a href="#" onClick={(e) => e.preventDefault()} style={{ color: 'var(--color-gray-600)' }}>
                Privacy Policy
              </a>
              <span>*</span>
              <a href="#" onClick={(e) => e.preventDefault()} style={{ color: 'var(--color-gray-600)' }}>
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
